import { openDB } from 'idb';
import jsmediatags from 'jsmediatags';

const DB_NAME = 'beatseek-local-tracks';
const STORE_NAME = 'tracks';
const DB_VERSION = 1;
const MAX_TRACKS = 10;
const MAX_SIZE_MB = 100;

// Initialize IndexedDB
export const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    },
  });
};

// Extract metadata from audio file
export const extractMetadata = (file) => {
  return new Promise((resolve, reject) => {
    jsmediatags.read(file, {
      onSuccess: (tag) => {
        const { title, artist, album, picture } = tag.tags;
        
        // Convert picture to data URL if available
        let artwork = null;
        if (picture) {
          const { data, format } = picture;
          let base64String = '';
          for (let i = 0; i < data.length; i++) {
            base64String += String.fromCharCode(data[i]);
          }
          artwork = `data:${format};base64,${window.btoa(base64String)}`;
        }

        resolve({
          title: title || file.name.replace(/\.[^/.]+$/, ''),
          artist: artist || 'Unknown Artist',
          album: album || 'Unknown Album',
          artwork
        });
      },
      onError: (error) => {
        console.warn('Could not read metadata, using filename:', error);
        // Fallback to filename
        resolve({
          title: file.name.replace(/\.[^/.]+$/, ''),
          artist: 'Unknown Artist',
          album: 'Unknown Album',
          artwork: null
        });
      }
    });
  });
};

// Get audio duration
export const getAudioDuration = (file) => {
  return new Promise((resolve) => {
    const audio = new Audio();
    audio.preload = 'metadata';
    
    audio.onloadedmetadata = () => {
      window.URL.revokeObjectURL(audio.src);
      resolve(audio.duration);
    };
    
    audio.onerror = () => {
      resolve(0);
    };
    
    audio.src = URL.createObjectURL(file);
  });
};

// Save track to IndexedDB
export const saveLocalTrack = async (file) => {
  try {
    const db = await initDB();
    
    // Check current track count
    const existingTracks = await db.getAll(STORE_NAME);
    if (existingTracks.length >= MAX_TRACKS) {
      throw new Error(`Maximum ${MAX_TRACKS} tracks allowed for optimal performance`);
    }
    
    // Check total size
    const totalSize = existingTracks.reduce((sum, t) => sum + t.size, 0) + file.size;
    const totalSizeMB = totalSize / (1024 * 1024);
    if (totalSizeMB > MAX_SIZE_MB) {
      throw new Error(`Maximum ${MAX_SIZE_MB}MB total size exceeded`);
    }
    
    // Extract metadata
    const metadata = await extractMetadata(file);
    const duration = await getAudioDuration(file);
    
    // Create track object
    const track = {
      id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      track_id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: metadata.title,
      artist: metadata.artist,
      album: metadata.album,
      artwork: metadata.artwork,
      duration_ms: Math.floor(duration * 1000),
      source: 'local',
      filename: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
      fileData: file // Store the actual file
    };
    
    // Save to IndexedDB
    await db.put(STORE_NAME, track);
    
    return track;
  } catch (error) {
    console.error('Error saving local track:', error);
    throw error;
  }
};

// Get all local tracks
export const getLocalTracks = async () => {
  try {
    const db = await initDB();
    return await db.getAll(STORE_NAME);
  } catch (error) {
    console.error('Error getting local tracks:', error);
    return [];
  }
};

// Get single local track
export const getLocalTrack = async (id) => {
  try {
    const db = await initDB();
    return await db.get(STORE_NAME, id);
  } catch (error) {
    console.error('Error getting local track:', error);
    return null;
  }
};

// Delete local track
export const deleteLocalTrack = async (id) => {
  try {
    const db = await initDB();
    await db.delete(STORE_NAME, id);
  } catch (error) {
    console.error('Error deleting local track:', error);
    throw error;
  }
};

// Clear all local tracks (on logout)
export const clearAllLocalTracks = async () => {
  try {
    const db = await initDB();
    await db.clear(STORE_NAME);
  } catch (error) {
    console.error('Error clearing local tracks:', error);
    throw error;
  }
};

// Create object URL for playback
export const createTrackURL = (track) => {
  if (track.fileData) {
    return URL.createObjectURL(track.fileData);
  }
  return null;
};
